import type { WeaponDefinition, WeaponId, AmmoType } from './types'
import { WEAPON_DEFINITIONS, WEAPON_ORDER } from './definitions'
import { PlayerVitality } from '../player/vitality'

export type AmmoChangeCallback = (type: Exclude<AmmoType, 'energy' | 'none'>, count: number) => void

export class AmmoInventory {
  private reserves: Record<Exclude<AmmoType, 'energy' | 'none'>, number> = {
    spread: 50,
    missile: 15,
    gyrex: 20,
    cannon: 30,
  }
  private changeCallbacks: AmmoChangeCallback[] = []

  onChange(cb: AmmoChangeCallback): () => void {
    this.changeCallbacks.push(cb)
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter((c) => c !== cb)
    }
  }

  private notifyChange(type: Exclude<AmmoType, 'energy' | 'none'>) {
    const val = this.getAmmo(type)
    for (let i = 0; i < this.changeCallbacks.length; i++) {
      this.changeCallbacks[i](type, val)
    }
  }

  getAmmo(type: Exclude<AmmoType, 'energy' | 'none'>): number {
    return this.reserves[type] ?? 0
  }

  setAmmo(type: Exclude<AmmoType, 'energy' | 'none'>, amount: number): void {
    this.reserves[type] = Math.max(0, amount)
    this.notifyChange(type)
  }

  addAmmo(type: Exclude<AmmoType, 'energy' | 'none'>, amount: number): void {
    this.reserves[type] = (this.reserves[type] ?? 0) + Math.max(0, amount)
    this.notifyChange(type)
  }

  consumeAmmo(type: Exclude<AmmoType, 'energy' | 'none'>, amount: number): boolean {
    const current = this.reserves[type] ?? 0
    if (current < amount) return false
    this.reserves[type] -= amount
    this.notifyChange(type)
    return true
  }
}

export type WeaponChangeCallback = (weapon: WeaponDefinition) => void
export type WeaponFireCallback = (weapon: WeaponDefinition) => void

export class WeaponInventory {
  readonly ammo = new AmmoInventory()
  private activeIndex = 0
  private weaponList: WeaponDefinition[]
  private changeCallbacks: WeaponChangeCallback[] = []
  private fireCallbacks: WeaponFireCallback[] = []
  private cooldownTimer = 0

  constructor(initialWeapons: WeaponId[] = WEAPON_ORDER) {
    this.weaponList = initialWeapons.map((id) => WEAPON_DEFINITIONS[id])
  }

  get weapons(): ReadonlyArray<WeaponDefinition> {
    return this.weaponList
  }

  get activeWeapon(): WeaponDefinition {
    return this.weaponList[this.activeIndex]
  }

  get activeSlot(): number {
    return this.activeWeapon.slot
  }

  onWeaponChange(cb: WeaponChangeCallback): () => void {
    this.changeCallbacks.push(cb)
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter((c) => c !== cb)
    }
  }

  onFire(cb: WeaponFireCallback): () => void {
    this.fireCallbacks.push(cb)
    return () => {
      this.fireCallbacks = this.fireCallbacks.filter((c) => c !== cb)
    }
  }

  private notifyWeaponChange() {
    for (let i = 0; i < this.changeCallbacks.length; i++) {
      this.changeCallbacks[i](this.activeWeapon)
    }
  }

  private notifyFire() {
    for (let i = 0; i < this.fireCallbacks.length; i++) {
      this.fireCallbacks[i](this.activeWeapon)
    }
  }

  nextWeapon(): WeaponDefinition {
    this.activeIndex = (this.activeIndex + 1) % this.weaponList.length
    this.notifyWeaponChange()
    return this.activeWeapon
  }

  prevWeapon(): WeaponDefinition {
    this.activeIndex = (this.activeIndex - 1 + this.weaponList.length) % this.weaponList.length
    this.notifyWeaponChange()
    return this.activeWeapon
  }

  selectWeapon(id: WeaponId): boolean {
    const idx = this.weaponList.findIndex((w) => w.id === id)
    if (idx === -1) return false
    if (this.activeIndex !== idx) {
      this.activeIndex = idx
      this.notifyWeaponChange()
    }
    return true
  }

  selectSlot(slotNumber: number): boolean {
    const idx = this.weaponList.findIndex((w) => w.slot === slotNumber)
    if (idx === -1) return false
    if (this.activeIndex !== idx) {
      this.activeIndex = idx
      this.notifyWeaponChange()
    }
    return true
  }

  fallbackToLastResort(): void {
    this.selectWeapon('blade')
  }

  update(dt: number) {
    if (this.cooldownTimer > 0) {
      this.cooldownTimer = Math.max(0, this.cooldownTimer - dt)
    }
  }

  canFire(vitality: PlayerVitality): boolean {
    if (this.cooldownTimer > 0) return false

    const weapon = this.activeWeapon
    if (weapon.ammoType === 'none') return true

    if (weapon.ammoType === 'energy') {
      return vitality.state.energy >= weapon.ammoCost
    }

    return this.ammo.getAmmo(weapon.ammoType) >= weapon.ammoCost
  }

  consumeFiringCost(vitality: PlayerVitality): boolean {
    if (!this.canFire(vitality)) return false

    const weapon = this.activeWeapon
    this.cooldownTimer = 1.0 / weapon.fireRate

    if (weapon.ammoType === 'none') {
      this.notifyFire()
      return true
    }

    if (weapon.ammoType === 'energy') {
      const ok = vitality.expendEnergy(weapon.ammoCost)
      if (ok) {
        this.notifyFire()
      }
      if (vitality.state.energy <= 0) {
        // Weapon of last resort trigger!
        this.fallbackToLastResort()
      }
      return ok
    }

    const ok = this.ammo.consumeAmmo(weapon.ammoType, weapon.ammoCost)
    if (ok) {
      this.notifyFire()
    }
    return ok
  }
}
